import {isTraceChannel, TRACE_CHANNELS, type TraceChannel} from './channels';

export type Trace = {
  (event: string, data?: TraceData): void;
  enabled: () => boolean;
};

export type TraceConfig = {
  /** `envManager`'s `appEnv`; tracing works only in `dev | stage1 | stage2 | rc`. */
  appEnv: string | undefined;
  /**
   * Mobile only: comma-separated channel list from `.env` (`TRACE_CHANNELS`,
   * passed as `envManager`'s `traceChannels`).
   * When passed, `localStorage` and the console API are not used.
   */
  channels?: string;
};

export type TraceConsole = {
  disable: (channel: TraceChannel) => void;
  disableAll: () => void;
  enable: (channel: TraceChannel) => void;
  enableAll: () => void;
  list: () => void;
};

export type TraceData = Record<string, unknown>;

declare global {
  var ycTrace: TraceConsole | undefined;
}

const ALLOWED_ENVS: ReadonlySet<string> = new Set([
  'dev',
  'rc',
  'stage1',
  'stage2',
]);
const STORAGE_KEY = 'yc:trace';
const ALL_CHANNELS = Object.keys(TRACE_CHANNELS) as TraceChannel[];

type State = {
  allowed: boolean;
  appEnv: string | undefined;
  enabled: Set<TraceChannel>;
  /** `false` on mobile, where channels come from `.env` only. */
  persist: boolean;
};

let state: null | State = null;

const warnUnknown = (names: string[], origin: string) => {
  if (!names.length) return;

  globalThis.console.warn(
    `[trace] unknown channel(s) in ${origin}: ${names.join(', ')}. Valid: ${ALL_CHANNELS.join(', ')}`,
  );
};

const toChannels = (names: unknown[], origin: string) => {
  const channels = new Set<TraceChannel>();
  const unknown: string[] = [];

  names.forEach(name => {
    const trimmed = String(name).trim();

    if (!trimmed) return;

    if (isTraceChannel(trimmed)) {
      channels.add(trimmed);
    } else {
      unknown.push(trimmed);
    }
  });

  warnUnknown(unknown, origin);

  return channels;
};

const getStorage = (): Storage | undefined => {
  try {
    return typeof globalThis.localStorage === 'undefined'
      ? undefined
      : globalThis.localStorage;
  } catch {
    return undefined;
  }
};

const readStorage = (): Set<TraceChannel> => {
  try {
    const raw = getStorage()?.getItem(STORAGE_KEY);

    if (!raw) return new Set();

    const parsed: unknown = JSON.parse(raw);

    return Array.isArray(parsed)
      ? toChannels(parsed, `localStorage "${STORAGE_KEY}"`)
      : new Set();
  } catch {
    return new Set();
  }
};

const writeStorage = (channels: Set<TraceChannel>) => {
  try {
    getStorage()?.setItem(
      STORAGE_KEY,
      JSON.stringify(Array.from(channels).sort()),
    );
  } catch {
    // Storage may be blocked (Safari private mode, sandboxed iframes).
  }
};

const onStorage = (event: StorageEvent) => {
  if (state?.persist && (event.key === STORAGE_KEY || event.key === null)) {
    state.enabled = readStorage();
  }
};

const isEnabled = (channel: TraceChannel) =>
  Boolean(state?.allowed && state.enabled.has(channel));

const refuse = (action: string) => {
  const reason = !state?.allowed
    ? `tracing is disabled for appEnv "${state?.appEnv ?? 'not configured'}"`
    : 'channels come from TRACE_CHANNELS in .env here';

  globalThis.console.warn(`[trace] ${action} refused: ${reason}`);
};

const update = (
  action: string,
  mutate: (enabled: Set<TraceChannel>) => void,
) => {
  if (!state?.allowed || !state.persist) {
    refuse(action);

    return;
  }

  mutate(state.enabled);
  writeStorage(state.enabled);
};

const checkChannel = (channel: string): channel is TraceChannel => {
  if (isTraceChannel(channel)) return true;

  warnUnknown([channel], 'ycTrace');

  return false;
};

/**
 * Browser-console control. Exposed as `globalThis.ycTrace` by `configureTrace`
 * on web in allowed envs.
 */
export const traceConsole: TraceConsole = {
  disable: channel => {
    if (!checkChannel(channel)) return;

    update(`disable('${channel}')`, enabled => enabled.delete(channel));
  },
  disableAll: () => update('disableAll()', enabled => enabled.clear()),
  enable: channel => {
    if (!checkChannel(channel)) return;

    update(`enable('${channel}')`, enabled => enabled.add(channel));
  },
  enableAll: () =>
    update('enableAll()', enabled =>
      ALL_CHANNELS.forEach(channel => enabled.add(channel)),
    ),
  list: () => {
    globalThis.console.table(
      ALL_CHANNELS.map(channel => ({
        channel,
        description: TRACE_CHANNELS[channel],
        enabled: isEnabled(channel),
      })),
    );
  },
};

/**
 * Called by `envManager.__setEnv`. Hard-off unless `appEnv` is
 * `dev | stage1 | stage2 | rc`.
 */
export const configureTrace = ({appEnv, channels}: TraceConfig) => {
  const allowed = typeof appEnv === 'string' && ALLOWED_ENVS.has(appEnv);
  const persist = channels === undefined;

  state = {
    allowed,
    appEnv,
    enabled: !allowed
      ? new Set()
      : persist
        ? readStorage()
        : toChannels(channels.split(','), 'TRACE_CHANNELS'),
    persist,
  };

  if (!allowed || !persist || !getStorage()) return;

  globalThis.ycTrace = traceConsole;

  // Re-adding the same listener is a no-op, so repeat calls are safe.
  globalThis.addEventListener?.('storage', onStorage);
};

const stringify = (data: TraceData) => {
  try {
    return JSON.stringify(data);
  } catch {
    return '"[unserializable]"';
  }
};

/**
 * Creates a tracer for one channel. Safe at module top level: the channel
 * state is checked on every call, and nothing prints before `configureTrace`.
 *
 * Payload: ids, statuses, counters, timings and booleans only. Never tokens,
 * emails, names, phone numbers or whole API objects. Guard costly payloads
 * with `trace.enabled()`.
 */
export const createTrace = (channel: TraceChannel): Trace => {
  const trace = (event: string, data: TraceData = {}) => {
    if (!isEnabled(channel)) return;

    // `globalThis.console` survives `drop_console` / `transform-remove-console`.
    globalThis.console.log(
      `[trace:${channel}] ${new Date().toISOString()} ${event} ${stringify(data)}`,
    );
  };

  trace.enabled = () => isEnabled(channel);

  return trace;
};
